import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { assetsApi } from '../../api/assets';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';
import { formatDate } from '../../utils/formatDate';

export const ASSET_STATUS_LABELS: Record<string, string> = {
  available: 'Available', assigned: 'Assigned', in_use: 'In Use', in_transit: 'In Transit', under_maintenance: 'Under Maintenance',
  damaged: 'Damaged', lost: 'Lost', stolen: 'Stolen', retired: 'Retired', disposed: 'Disposed',
};

interface CreateAssetForm {
  name: string; assetType?: string; manufacturer?: string; model?: string; serialNumber?: string; supplier?: string;
  purchaseDate?: string; purchaseCost?: number;
}

export function AssetsListPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, reset } = useForm<CreateAssetForm>();

  const { data, isLoading } = useQuery({
    queryKey: ['assets', statusFilter, search],
    queryFn: () => assetsApi.list({ status: statusFilter || undefined, search: search || undefined }),
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateAssetForm) => assetsApi.create({ ...input, purchaseCost: input.purchaseCost ? Number(input.purchaseCost) : undefined }),
    onSuccess: (result) => {
      showSuccess(`Asset ${result.data.assetNumber} registered.`);
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      setShowCreateForm(false);
      reset();
      navigate(`/logistics/assets/${result.data.id}`);
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const assets = data?.data ?? [];

  return (
    <div className="page">
      <PageHeader title="Asset Register" description="Track organizational assets - custody, movement, and disposal." />

      <SectionHeader title="Assets" actions={
        <>
          <input placeholder="Search name, number, serial…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {Object.entries(ASSET_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button onClick={() => setShowCreateForm((s) => !s)}>{showCreateForm ? 'Cancel' : 'Register Asset'}</button>
        </>
      } />

      {showCreateForm && (
        <form className="inline-form" onSubmit={handleSubmit((d) => createMutation.mutate(d))}>
          <label>Name <input {...register('name', { required: true })} /></label>
          <label>Asset Type <input {...register('assetType')} /></label>
          <label>Manufacturer <input {...register('manufacturer')} /></label>
          <label>Model <input {...register('model')} /></label>
          <label>Serial Number <input {...register('serialNumber')} /></label>
          <label>Supplier <input {...register('supplier')} /></label>
          <label>Purchase Date <input type="date" {...register('purchaseDate')} /></label>
          <label>Purchase Cost <input type="number" step="0.01" {...register('purchaseCost')} /></label>
          <button type="submit" disabled={createMutation.isPending}>Register</button>
        </form>
      )}

      {isLoading ? <div className="loading-state"><span className="loading-spinner" />Loading…</div> : (
        <DataTable
          rows={assets}
          rowKey={(a) => a.id}
          columns={[
            { header: 'Asset Number', render: (a) => <a onClick={() => navigate(`/logistics/assets/${a.id}`)} className="table-link">{a.asset_number}</a> },
            { header: 'Name', render: (a) => a.name },
            { header: 'Type', render: (a) => a.asset_type ?? '-' },
            { header: 'Serial Number', render: (a) => a.serial_number ?? '-' },
            { header: 'Purchased', render: (a) => a.purchase_date ? formatDate(a.purchase_date) : '-' },
            { header: 'Condition', render: (a) => a.condition_status },
            { header: 'Status', render: (a) => <StatusBadge status={a.status} label={ASSET_STATUS_LABELS[a.status]} /> },
          ]}
        />
      )}
    </div>
  );
}
